// Mask primitive — plan §4.1: coverage from the surface's own geometry (snow, dirt).
//
// SCOPE: only the SLOPE axis is available in the fragment stage today.  The chunk's geometric
// normal (z-up, slope_scale already applied worker-side) is the sole per-fragment geometry
// input; elevation and curvature are NOT uniforms of this shader.  Elevation-based coverage is
// already handled one level up, by the splat field's `elevationBand`/`cliffBand` rules
// (Phase 5) — the cheaper place for it, since it costs no per-fragment work at all.  If
// per-fragment elevation is ever wanted (a soft snowline WITHIN one splat region), add a
// u_baseElevation uniform + a matElevation() indirection and a second maskVal overload.
//
// The geo normal is reached through the matGeoNormal() INDIRECTION (emitted by the assembler
// next to matPixelSize), never the u_geoNormal texture directly: that texture only exists when
// lighting is enabled, and a mask layer must still assemble in the Phase-2 unlit path.  The
// indirection also gives the mask PER-SAMPLE slope granularity (matGeoNormal fetches the
// chunk's normal lattice) — the Phase-3 per-chunk uniform collapsed slope to one value per
// chunk.
//
// CRITICAL (2026-08-09 snow-goes-black fix): u_geoNormal has `slope_scale` ALREADY APPLIED
// worker-side (default 4.0 — see TerrainMaterialManager's u_slopeScale default and the
// LightingStage header).  Reading `1 - n.z` straight off that texture therefore measures an
// EXAGGERATED slope, ~slope_scale times too steep in tangent terms: a gentle 8° hillside reads
// as ~30°, so snow's coverage thresholds (0.3..0.65 ≈ 45°..65° in TRUE slope) were being blown
// past on almost any visible hillside.  The mask then went to 0, the `maskedBy` blend replaced
// the white snow with the dark `uncoveredColor` rock (~0.33), and hillshade multiplied that down
// to near-black — the reported "white snow becomes black on a hillside".
//
// So maskVal DE-EXAGGERATES first: convert the stored normal back to a true surface slope by
// dividing its tangent by the same slope_scale the worker multiplied in (matAmbientSlopeScale()
// is that scale, and already falls back to 1.0 in the unlit path where matGeoNormal() is a flat
// (0,0,1) — so the unlit assemble is unchanged: slope 0, coverage 1).
//
// NOTE (repo hazard): no backticks, no GLSL-ES-300 reserved identifiers inside the literal.
export const MASK_GLSL = `
// Slope coverage.  slope is measured as 1 - n.z of the TRUE (un-exaggerated) surface normal
// (0 = flat, 1 = vertical), so the thresholds read naturally and are independent of the
// display-only slope_scale: slopeStart = where coverage begins to thin, slopeEnd = where it is
// gone.  Returns 1 where the material should be fully present, 0 where it is fully removed.
float maskVal(float slopeStart, float slopeEnd) {
  vec3 gn = matGeoNormal();
  float nz = clamp(gn.z, 1e-5, 1.0);
  // tan(theta) of the EXAGGERATED normal, then undo the worker's slope_scale multiply.
  float tanExagg = length(gn.xy) / nz;
  float tanTrue = tanExagg / max(matAmbientSlopeScale(), 1e-5);
  // Back to a z-component (cos theta) of the true normal.
  float slope = 1.0 - inversesqrt(1.0 + tanTrue * tanTrue);
  float lo = min(slopeStart, slopeEnd);
  float hi = max(slopeStart, slopeEnd);
  return 1.0 - smoothstep(lo, max(hi, lo + 1e-5), slope);
}
`;